import React, { useEffect } from 'react';
import { useRouter } from 'next/router';
import dynamic from 'next/dynamic';
import { useMutation } from '@apollo/client';
import { connect, useDispatch } from 'react-redux';
import { usePathname } from 'next/navigation';
import { noTopSafeArea, useBridgeState } from '@digitaz/bridge-sdk-client';
import * as actionCreators from '../../actions';
import { togglePromotionModalView } from '../../actions';
import { sendAccessToken } from '../../api/mutations';
import { BlockerDetector } from '../Banner/BlockerDetector';
import { Loader } from '../Loader';
import { useBackHistoryStorage } from '../../hooks/useBackHistoryStorage';
import {
  longPromotionHashes,
  shortPromotionHashes,
  urlHashes
} from '../../utils/helpers/constants';
import useScreenSize from '../../hooks/useScreenSize';
import { useFirstInteraction } from '../../hooks/useFirstInteraction';
import { useAutoScrollTop } from '../../hooks/useAutoScrollTop';
import { WelcomeBanner } from '../ReplenishmentWelcomeBanner';
import { Footer } from '../Footer';
import { MainLayout } from '../ui/Layout';
import { InitialFlexibleHeader } from '../FlexibleHeader/InitialFlexibleHeader';
import { HeaderWrapperBlock } from './styled';
import { RootState } from '../../store/types';
import { LayoutProps } from './types';
import { AuthProvider, useAuth } from '../../context/AuthContext';
import {
  AuthMutation,
  AuthMutationVariables,
  CompanyNotification
} from '../../graphql/graphql';
import { broadcastAuthEvent } from '../../utils/authSync';
import { useCloseReplenishmentNotification } from '../../hooks/useCloseReplenishmentNotification';
import { isSuccessfulSignIn } from '../../utils/auth';

const BannersContainer = dynamic(() => import('../Banner/BannersContainer'), {
  ssr: false
});

const FlexibleHeader = dynamic(
  () => import('../FlexibleHeader').then(mod => mod.FlexibleHeader),
  { ssr: false, loading: InitialFlexibleHeader }
);

const PromotionModalView = dynamic(
  () => import('../PromotionModalView').then(mod => mod.PromotionModalView),
  {
    ssr: false
  }
);

const SideMenuModal = dynamic(
  () => import('../SideMenuModal').then(mod => mod.SideMenuModal),
  { ssr: false }
);

const MapModal = dynamic(
  () => import('../ui/MapModal').then(mod => mod.MapModal),
  {
    ssr: false
  }
);

const AuthModal = dynamic(() => import('../AuthModal'), {
  ssr: false
});

const TabBarNavigation = dynamic(() =>
  import('../TabBarNavigation').then(mod => mod.TabBarNavigation)
);

interface ReplenishmentBannerProps {
  notifications?: CompanyNotification[];
  isOpen?: boolean;
}

const ReplenishmentBanner: React.FC<ReplenishmentBannerProps> = ({
  notifications,
  isOpen
}) => {
  const { profile } = useAuth();
  const notification = notifications?.[0];
  const closeNotificationsModal = useCloseReplenishmentNotification(
    profile?.id,
    notifications
  );

  if (!notification) return null;

  return (
    <WelcomeBanner
      isOpen={isOpen}
      hide={closeNotificationsModal}
      id={notification.id}
      description={notification.description}
      title={notification.title}
      imageUrl={notification.imageUrl}
    />
  );
};

let isLoading: boolean | null = null;
const mapStateToProps = (state: RootState) => ({
  isVisible: state.drawerUI.isVisible,
  isSideMenuVisible: state.sideMenuModal?.isVisible,
  isPromotionModalView: state.promotionModalView?.isVisible,
  isAuthVisible: state?.authModal?.isVisible,
  replenishmentNotifications: state?.replenishmentNotifications,
  showNotificationsModal: state?.replenishmentNotificationsModal?.isVisible
});

const LayoutComponent: React.FC<LayoutProps> = ({
  isBanner,
  children,
  setBlockerDetector,
  toggleSideMenuModal,
  isAuthVisible,
  isSideMenuVisible,
  isPromotionModalView,
  toggleAuthModal,
  isBottomNavigation = true,
  isShowHeader = true,
  isFixedHeader = false,
  isShowCloseButton = true,
  isCurrentItemPage = false,
  isHomePage = false,
  isSafeAreaTop = false,
  replenishmentNotifications,
  showNotificationsModal,
  isBackgroundBanner = false,
  isShowFooter = true,
  hideMobileHeader = false,
  isShowLocations = true
}) => {
  const router = useRouter();
  const dispatch = useDispatch();
  const { isWebView } = useBridgeState();
  const { isSmall } = useScreenSize();
  const hasUserInteracted = useFirstInteraction();
  const shouldRenderBanners = !isHomePage || hasUserInteracted;
  const pathname = usePathname();
  const hasAuthHash = router.asPath.includes(urlHashes.authentication);

  const isSearchPage = router.pathname === '/[...search]';
  const isAdCreateEditPage =
    router.pathname === '/items/new' || router.pathname === '/items/edit';

  useEffect(() => {
    if (isWebView) document.body.classList.add('webview');

    if (isSafeAreaTop && isWebView) {
      document.body.classList.add('safe-area-top');
    } else {
      document.body.classList.remove('safe-area-top');
    }
  }, [isSafeAreaTop, isWebView]);

  const closeAuthModalHandler = () => {
    toggleAuthModal?.(false);

    router.push(`${router.asPath.split('#')[0]}`, undefined, {
      shallow: true
    });
  };

  const accessToken = Array.isArray(router?.query.access_token)
    ? router.query.access_token[0]
    : router?.query.access_token;

  const [mutateToken] = useMutation<AuthMutation, AuthMutationVariables>(
    sendAccessToken,
    {
      onCompleted: data => {
        isLoading = false;
        if (isSuccessfulSignIn(data)) {
          broadcastAuthEvent('LOGIN');
        }
      }
    }
  );

  useBackHistoryStorage();

  useAutoScrollTop(!!isCurrentItemPage, pathname);

  useEffect(() => {
    if (!isWebView) return;

    noTopSafeArea('', url => router.replace(url));

    const handleBeforeHistoryChange = (path: string) => {
      noTopSafeArea(path, url => router.replace(url));
    };

    router.events.on('beforeHistoryChange', handleBeforeHistoryChange);

    return () => {
      router.events.off('beforeHistoryChange', handleBeforeHistoryChange);
    };
  }, [isWebView]);

  useEffect(() => {
    if (router?.isReady) {
      if (accessToken) {
        isLoading = true;
        mutateToken({
          variables: { accessToken }
        });
      } else {
        isLoading = false;
      }
    }
  }, [accessToken]);

  useEffect(() => {
    toggleAuthModal?.(!!router.asPath.match(urlHashes.authentication));
    toggleSideMenuModal?.(!!router.asPath.match(urlHashes.more));

    dispatch(
      togglePromotionModalView(
        shortPromotionHashes.some(hash => router.asPath.match(hash))
      )
    );

    const handleHashChange = (url: string) => {
      toggleAuthModal?.(!!url.match(urlHashes.authentication));
      toggleSideMenuModal?.(!!url.match(urlHashes.more));

      dispatch(
        togglePromotionModalView(
          !!longPromotionHashes.some(hash => url.match(hash))
        )
      );
    };

    router?.events.on('hashChangeComplete', handleHashChange);

    return () => {
      router?.events.off('hashChangeComplete', handleHashChange);
    };
  }, []);

  const isAuthInProgress =
    (!router?.isReady && isLoading === null && !accessToken) ||
    (!!accessToken && isLoading !== false);

  return (
    <MainLayout isWebView={isWebView}>
      <AuthProvider isAuthInProgress={isAuthInProgress}>
        {!!replenishmentNotifications?.length && (
          <ReplenishmentBanner
            isOpen={showNotificationsModal}
            notifications={replenishmentNotifications}
          />
        )}
        {shouldRenderBanners && (
          <BannersContainer
            {...{ isBackgroundBanner, isBanner, isCurrentItemPage }}
          />
        )}
        {isShowHeader && (
          <HeaderWrapperBlock
            $isSearchPage={isSearchPage}
            $isCurrentItemPage={isCurrentItemPage}
            $isAdCreateEditPage={isAdCreateEditPage}
            $hideMobileHeader={hideMobileHeader}
          >
            <FlexibleHeader
              isFixedHeader={isWebView || isFixedHeader}
              isShowCloseButton={isShowCloseButton}
            />
          </HeaderWrapperBlock>
        )}
        <BlockerDetector setBlockerDetector={setBlockerDetector} />
        {children}
        <Loader pageLoader={true} />
        {isBottomNavigation && <TabBarNavigation />}
        {!(isWebView || isSideMenuVisible) && isShowFooter && (
          <Footer
            isTabBarExist={isBottomNavigation}
            isBackgroundBanner={isBackgroundBanner && !isSmall}
            isShowLocations={isShowLocations}
          />
        )}
        {isSideMenuVisible && <SideMenuModal />}
        {isPromotionModalView && <PromotionModalView pageScenario="ad_page" />}
        {isAuthVisible && hasAuthHash && (
          <AuthModal
            isOpen={isAuthVisible}
            modalId="auth"
            closeHandler={closeAuthModalHandler}
          />
        )}
        <MapModal />
      </AuthProvider>
    </MainLayout>
  );
};

export const Layout = connect(mapStateToProps, actionCreators)(LayoutComponent);
